import axios, { AxiosHeaders } from 'axios';
import { BASE_URL, ROOT_URL } from './ApiConfig';

// Auth lives in HTTP-only access_token / refresh_token cookies set by the backend.
// Every request to our API must carry credentials, and a 401 gets one refresh + retry.

const REFRESH_URL = `${ROOT_URL}/master-user/refresh`;

// A 401 from these means bad credentials, not an expired access token.
const NO_REFRESH_PATHS = [
  '/master-user/login',
  '/master-user/refresh',
  '/master-user/logout',
  '/co-users/login',
  '/platform-admin/',
];

// Platform admin auth is Bearer-token only — never attach tenant cookies.
const NO_COOKIE_PATHS = ['/platform-admin/'];

const nativeFetch = window.fetch.bind(window);

let isSignedIn = () => false;
let onSessionExpired = () => {};
let refreshPromise = null;
let expiredHandled = false;

const API_ORIGIN = new URL(BASE_URL).origin;

function isApiUrl(url) {
  try {
    return new URL(url).origin === API_ORIGIN;
  } catch {
    return false;
  }
}

const usesCookieAuth = (url) => !NO_COOKIE_PATHS.some((path) => url.includes(path));

const shouldRefresh = (url) =>
  isSignedIn() && usesCookieAuth(url) && !NO_REFRESH_PATHS.some((path) => url.includes(path));

// Cross-origin pages cannot add this header without passing CORS preflight, so the backend
// can reject cookie-authenticated writes that arrive without it (CSRF).
const CSRF_HEADER = 'X-Requested-With';
const CSRF_HEADER_VALUE = 'XMLHttpRequest';

// Thunks still build `Bearer ${token}` from state, which is now undefined for cookie sessions.
const isEmptyBearer = (value) =>
  typeof value === 'string' && /^(Bearer\s*(undefined|null)?\s*)?$/i.test(value.trim());

// The refresh token rotates on every use and the cookie is shared by all tabs, so tabs must
// take turns: a tab that waited while another refreshed reuses the new cookie instead of
// spending the old token.
const REFRESH_LOCK = 'trustchat-auth-refresh';
const LAST_REFRESH_KEY = 'authLastRefreshAt';

function readLastRefreshAt() {
  try {
    return Number(localStorage.getItem(LAST_REFRESH_KEY)) || 0;
  } catch {
    return 0;
  }
}

function postRefresh() {
  return nativeFetch(REFRESH_URL, {
    method: 'POST',
    credentials: 'include',
    headers: { [CSRF_HEADER]: CSRF_HEADER_VALUE },
  })
    .then((res) => {
      if (res.ok) {
        try {
          localStorage.setItem(LAST_REFRESH_KEY, String(Date.now()));
        } catch {
          /* storage unavailable; other tabs fall back to the server grace window */
        }
      }
      return res.ok;
    })
    .catch(() => false);
}

function refreshAcrossTabs() {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (!locks?.request) return postRefresh();

  const requestedAt = Date.now();
  return locks.request(REFRESH_LOCK, () =>
    readLastRefreshAt() >= requestedAt ? true : postRefresh(),
  );
}

function refreshSession() {
  if (!refreshPromise) {
    refreshPromise = refreshAcrossTabs()
      .catch(() => false)
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

function expireSession() {
  if (expiredHandled) return;
  expiredHandled = true;
  onSessionExpired();
}

/** Shared exit for HTTP refresh failure and socket `unauthorized`. */
export function forceAuthExpiry() {
  expireSession();
}

/** Cookie refresh for sockets / callers that need a new access_token before reconnect. */
export function tryRefreshSession() {
  return refreshSession();
}

/** Allow a new login in the same tab after a prior expiry redirected to /login. */
export function resetAuthExpiryGate() {
  expiredHandled = false;
}

async function recoverFromUnauthorized() {
  const refreshed = await refreshSession();
  if (!refreshed) expireSession();
  return refreshed;
}

function fetchUrl(input) {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  return input?.url || '';
}

function apiFetchHeaders(headers) {
  const cleaned = new Headers(headers);
  if (cleaned.has('Authorization') && isEmptyBearer(cleaned.get('Authorization'))) {
    cleaned.delete('Authorization');
  }
  cleaned.set(CSRF_HEADER, CSRF_HEADER_VALUE);
  return cleaned;
}

async function authFetch(input, init = {}) {
  const url = fetchUrl(input);
  if (!isApiUrl(url)) return nativeFetch(input, init);

  if (!usesCookieAuth(url)) {
    return nativeFetch(input, {
      ...init,
      credentials: 'omit',
    });
  }

  const options = {
    ...init,
    credentials: 'include',
    headers: apiFetchHeaders(init.headers),
  };
  const response = await nativeFetch(input, options);
  if (response.status !== 401 || !shouldRefresh(url)) return response;

  if (!(await recoverFromUnauthorized())) return response;

  // A 401 right after a successful refresh means the session itself is no longer valid.
  const retried = await nativeFetch(input, options);
  if (retried.status === 401) expireSession();
  return retried;
}

function axiosUrl(config) {
  const url = config?.url || '';
  if (/^https?:\/\//i.test(url)) return url;
  return `${config?.baseURL || ''}${url}`;
}

function onAxiosRequest(config) {
  const url = axiosUrl(config);
  if (!isApiUrl(url)) return config;

  if (!usesCookieAuth(url)) {
    config.withCredentials = false;
    return config;
  }

  config.withCredentials = true;
  const headers = AxiosHeaders.from(config.headers);
  if (headers.has('Authorization') && isEmptyBearer(headers.get('Authorization'))) {
    headers.delete('Authorization');
  }
  headers.set(CSRF_HEADER, CSRF_HEADER_VALUE);
  config.headers = headers;
  return config;
}

async function onAxiosError(error) {
  const config = error?.config;
  if (error?.response?.status !== 401 || !config || !shouldRefresh(axiosUrl(config))) {
    throw error;
  }
  if (config._authRetry) {
    expireSession();
    throw error;
  }

  config._authRetry = true;
  if (!(await recoverFromUnauthorized())) throw error;
  return axios(config);
}

export function installAuthHttp(options) {
  isSignedIn = options.isSignedIn;
  onSessionExpired = options.onSessionExpired;

  window.fetch = authFetch;
  axios.interceptors.request.use(onAxiosRequest);
  axios.interceptors.response.use(undefined, onAxiosError);
}
